import { cn } from '@/lib/utils';
import { ChevronDown, Eraser, Radio, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useApiLog } from '@/hooks/use-api-log';
import { apiLog  } from '@/lib/api-log';
import type {ApiCall} from '@/lib/api-log';

/**
 * Inspecteur d'API — le tiroir qui journalise chaque échange avec Dash Lite.
 *
 * Il n'existe que pour cette démonstration : il lit le journal alimenté par le
 * `fetch` instrumenté de `lib/dashlite.ts` et n'a aucune influence sur le
 * fonctionnement du site. Passez `VITE_SHOW_API_INSPECTOR=false` pour voir
 * Helvedoc tel qu'un visiteur le verrait.
 *
 * Ce qu'il permet de constater en naviguant :
 *
 * - `GET /site` n'est appelé qu'une fois, au démarrage ;
 * - la recherche est déboncée : une frappe rapide ne produit qu'un appel ;
 * - une fiche demandée sans accès répond `403 entitlement_required` ;
 * - le tunnel de paiement enchaîne `POST /payment` puis `POST /payment/claim` ;
 * - la clé `X-Site-Key` accompagne **tous** les appels, le `Bearer` seulement
 *   ceux qui exigent une session.
 */

const STATE_STYLES: Record<ApiCall['state'], string> = {
    pending: 'bg-caution/15 text-accent-700',
    success: 'bg-positive/15 text-positive',
    error: 'bg-critical/15 text-critical',
    'network-error': 'bg-critical/15 text-critical',
};

const METHOD_STYLES: Record<string, string> = {
    GET: 'text-brand-300',
    POST: 'text-emerald-300',
    DELETE: 'text-rose-300',
};

export function ApiInspector() {
    const calls = useApiLog();
    const [open, setOpen] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    // Raccourci clavier : Échap ferme le tiroir, comme toute surcouche.
    useEffect(() => {
        if (!open) {
            return;
        }

        function onKeyDown(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        }

        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open]);

    const errorCount = calls.filter((call) => call.state !== 'success' && call.state !== 'pending').length;

    return (
        <>
            {/* Pastille d'ouverture, toujours accessible en bas à droite. */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="bg-brand-950 hover:bg-brand-900 fixed right-4 bottom-4 z-40 flex items-center gap-2 rounded-full py-2.5 pr-4 pl-3.5 text-sm font-medium text-white shadow-lg transition-colors"
                >
                    <Radio className="size-4" aria-hidden />
                    Inspecteur d'API
                    <span
                        className={cn(
                            'rounded-full px-2 py-0.5 text-xs font-semibold',
                            errorCount > 0 ? 'bg-critical text-white' : 'bg-white/15 text-white',
                        )}
                    >
                        {calls.length}
                    </span>
                </button>
            )}

            {open && (
                <aside
                    role="complementary"
                    aria-label="Inspecteur des appels d'API"
                    className="border-brand-900 bg-brand-950 fixed inset-x-0 bottom-0 z-40 flex max-h-[65vh] flex-col rounded-t-xl border shadow-2xl sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-h-[70vh] sm:w-[34rem] sm:rounded-xl"
                >
                    <header className="border-brand-900 flex items-center justify-between border-b px-4 py-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                            <Radio className="text-brand-300 size-4" aria-hidden />
                            Inspecteur d'API
                            <span className="text-brand-300 font-mono text-xs font-normal">
                                {calls.length} appel{calls.length > 1 ? 's' : ''}
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => {
                                    apiLog.clear();
                                    setExpandedId(null);
                                }}
                                className="text-brand-200 hover:bg-brand-900 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs hover:text-white"
                            >
                                <Eraser className="size-3.5" aria-hidden />
                                Vider
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="Fermer l'inspecteur"
                                className="text-brand-200 hover:bg-brand-900 rounded-md p-1.5 hover:text-white"
                            >
                                <X className="size-4" aria-hidden />
                            </button>
                        </div>
                    </header>

                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {calls.length === 0 ? (
                            <p className="text-brand-300 px-4 py-8 text-center text-sm">
                                Aucun appel pour l'instant. Lancez une recherche : les requêtes s'afficheront ici, avec
                                leurs en-têtes et leurs réponses.
                            </p>
                        ) : (
                            <ul className="divide-brand-900 divide-y">
                                {calls.map((call) => (
                                    <InspectorRow
                                        key={call.id}
                                        call={call}
                                        expanded={expandedId === call.id}
                                        onToggle={() => setExpandedId(expandedId === call.id ? null : call.id)}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </aside>
            )}
        </>
    );
}

function InspectorRow({
    call,
    expanded,
    onToggle,
}: {
    call: ApiCall;
    expanded: boolean;
    onToggle: () => void;
}) {
    return (
        <li>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className="hover:bg-brand-900/60 flex w-full items-center gap-3 px-4 py-2.5 text-left font-mono text-xs"
            >
                <ChevronDown
                    className={cn('text-brand-400 size-3.5 shrink-0 transition-transform', expanded && 'rotate-180')}
                    aria-hidden
                />

                <span className={cn('w-14 shrink-0 font-semibold', METHOD_STYLES[call.method] ?? 'text-brand-300')}>
                    {call.method}
                </span>

                <span className="min-w-0 flex-1 truncate text-white/90">{call.endpoint}</span>

                {call.errorCode && (
                    <span className="text-critical hidden shrink-0 truncate sm:inline">{call.errorCode}</span>
                )}

                <span className={cn('shrink-0 rounded px-1.5 py-0.5 font-semibold', STATE_STYLES[call.state])}>
                    {call.state === 'pending' ? '···' : call.state === 'network-error' ? 'réseau' : call.status}
                </span>

                <span className="text-brand-400 w-12 shrink-0 text-right">
                    {call.durationMs === null ? '' : `${call.durationMs} ms`}
                </span>
            </button>

            {expanded && (
                <div className="bg-brand-950/70 space-y-3 px-4 pt-1 pb-4">
                    <InspectorBlock title="Requête" value={{ url: call.url, headers: call.requestHeaders }} />
                    {call.requestBody !== undefined && call.requestBody !== null && (
                        <InspectorBlock title="Corps envoyé" value={call.requestBody} />
                    )}
                    <InspectorBlock title="Réponse" value={call.responseBody} />
                </div>
            )}
        </li>
    );
}

function InspectorBlock({ title, value }: { title: string; value: unknown }) {
    return (
        <div>
            <p className="text-brand-300 mb-1 text-[11px] font-semibold tracking-wide uppercase">{title}</p>
            <pre className="border-brand-900 overflow-x-auto rounded-md border bg-black/40 px-3 py-2 font-mono text-[11px] leading-relaxed text-white/80">
                <code>{JSON.stringify(value, null, 2)}</code>
            </pre>
        </div>
    );
}
